import React, { useState, useRef, useCallback, useEffect } from "react";
import { collect, parse, apply, fileName, summarise } from "../../shared/backup";
import { useAccount } from "../../shared/auth";
import { ACCOUNT_KEY } from "../../shared/reconcile";

/**
 * Getting everything out, and back in.
 *
 * Restoring replaces the lot, so it says what is in the file and what it is
 * about to overwrite before it does anything. The reload afterwards is not
 * laziness: every screen reads its storage once on mount, so without it half
 * the app would still be showing what was there before, which is the most
 * confusing possible outcome of a successful restore.
 */

export default function BackupPanel({ onClose }) {
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [wiping, setWiping] = useState(false);
  const fileRef = useRef(null);
  const { signedIn } = useAccount();

  /* The server copy only. The browser is left alone deliberately: somebody
     asking to clear their account data has not asked to lose the collection
     they are looking at, and conflating the two would be the most expensive
     possible misreading of this button. */
  const wipe = useCallback(async () => {
    setWiping(true);
    setError("");
    try {
      const res = await fetch("/api/sync", { method: "DELETE" });
      if (!res.ok) throw new Error(String(res.status));
      const { removed } = await res.json();
      /* Without this the next sync would push it all straight back up, and
         the delete would look like it silently failed. */
      window.localStorage.removeItem(ACCOUNT_KEY);
      setConfirmWipe(false);
      setNote(
        removed
          ? "Deleted. Your account is empty; everything here is still on this device."
          : "Your account was already empty. Nothing on this device has changed."
      );
    } catch {
      setError("That could not be deleted just now. Nothing has been removed.");
    } finally {
      setWiping(false);
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const here = summarise(collect().entries);

  const save = useCallback(() => {
    const name = fileName();
    const blob = new Blob([JSON.stringify(collect(), null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
    setError("");
    setNote(`Saved ${name}. Keep it somewhere that isn't this phone.`);
  }, []);

  const take = useCallback((raw) => {
    const result = parse(raw);
    if (result.error) { setError(result.error); setPending(null); return; }
    setError("");
    setNote("");
    setPending(result);
  }, []);

  const onFile = useCallback(
    (file) => {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => take(String(reader.result || ""));
      reader.onerror = () => setError("That file could not be read.");
      reader.readAsText(file);
    },
    [take]
  );

  const restore = useCallback(() => {
    apply(pending.entries, undefined, pending.stamps);
    /* Every screen reads its storage once, on mount. Without a reload the
       collection would be the restored one and the lists would still be the
       old ones, which is worse than the restore having failed. */
    window.location.reload();
  }, [pending]);

  return (
    <>
      <div className="bk-scrim" onClick={onClose} aria-hidden="true" />
      <div className="bk" role="dialog" aria-modal="true" aria-label="Back up and restore">
        <div className="bk-head">
          <span>Back up everything</span>
          <button type="button" onClick={onClose} aria-label="Close">×</button>
        </div>

        <p className="bk-why">
          {signedIn
            ? "Your collection, lists, want list, goals and binder are in this browser and in your account. A backup file is the copy nobody else is holding — it works between browsers, and it keeps working if you ever stop signing in."
            : "Your collection, lists, want list, goals and binder live in this browser and nowhere else. There is no account holding a copy — clearing site data or changing phone loses all of it. A backup file is the answer to that."}
        </p>

        <div className="bk-now">
          <span className="bk-label">On this device</span>
          {here.length ? (
            <ul>{here.map((line) => <li key={line}>{line}</li>)}</ul>
          ) : (
            <p className="msg-empty">Nothing saved yet.</p>
          )}
        </div>

        <div className="bk-row">
          <button type="button" className="btn btn-primary" onClick={save}>
            Save a backup
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => fileRef.current?.click()}>
            Restore from a file
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="bk-file"
            aria-label="Choose a backup file"
            onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }}
          />
        </div>

        <label className="bk-paste">
          or paste a backup here
          <textarea
            rows={3}
            spellCheck={false}
            placeholder='{"format":"pokepricer-backup", …}'
            onChange={(e) => { const v = e.target.value.trim(); if (v) take(v); }}
          />
        </label>

        {/* What is in the file, and what it costs, before anything happens. */}
        {pending && (
          <div className="bk-confirm">
            <p className="bk-label">That backup holds</p>
            {pending.summary.length ? (
              <ul>{pending.summary.map((line) => <li key={line}>{line}</li>)}</ul>
            ) : (
              <p>settings only — no cards.</p>
            )}
            {pending.savedAt && (
              <p className="bk-when">Saved {new Date(pending.savedAt).toLocaleString()}</p>
            )}
            <p className="bk-warn">
              This replaces everything on this device. What is here now is gone unless
              you have saved it too.
            </p>
            <div className="bk-row">
              <button type="button" className="btn btn-primary" onClick={restore}>
                Replace everything
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setPending(null)}>
                Keep what I have
              </button>
            </div>
          </div>
        )}

        {/* Here rather than in a settings screen, because this is where
            somebody is already thinking about where their data is. Only shown
            signed in: with no account there is nothing on a server to remove,
            and offering to delete one would be alarming and untrue. */}
        {signedIn && (
          <div className="bk-danger">
            <span className="bk-label">Your account copy</span>
            {!confirmWipe ? (
              <>
                <p>
                  Removes everything this account is holding on the server. What is
                  in this browser stays, and so does your sign-in.
                </p>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => { setConfirmWipe(true); setError(""); setNote(""); }}
                >
                  Delete my account data
                </button>
              </>
            ) : (
              <>
                {/* Said before, not after. The server copy is the one somebody
                    cannot get back from a drawer. */}
                <p className="bk-warn">
                  This cannot be undone. Save a backup first if you want to keep any
                  of it — the copy in this browser will be pushed back up next time
                  you change something, so sign out afterwards if you meant all of it
                  gone.
                </p>
                <div className="bk-row">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={wiping}
                    onClick={wipe}
                  >
                    {wiping ? "Deleting…" : "Yes, delete it"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={wiping}
                    onClick={() => setConfirmWipe(false)}
                  >
                    Keep it
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {error && <p className="msg-error">{error}</p>}
        {note && !error && <p className="bk-note">{note}</p>}
      </div>
    </>
  );
}
